import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import GuestDetailsModal from '../components/GuestDetailsModal';
import CountryCombobox from '../components/CountryCombobox';
import { DownloadIcon, SearchIcon, MoreVerticalIcon, EyeIcon } from '../components/Icons';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { logoBase64 } from '../logo';
import html2canvas from 'html2canvas';
import toast from 'react-hot-toast';
import { isLikelyPersonalEmail } from '../utils/emailValidation';
import AddGuestModal from '../components/AddGuestModal';
import './Events.css';



const formatEventDate = (start, end, isMulti) => {
  if (!start) return '—';
  const s = new Date(start);
  if (!isMulti || !end) return s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  const e = new Date(end);
  if (s.toDateString() === e.toDateString()) return s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.getDate()} - ${e.getDate()} ${s.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
  }
  return `${s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${e.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

export default function GuestList() {
  const { faculty } = useAuth();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEvent, setFilterEvent] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [viewPass, setViewPass] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const loadData = () => {
    setLoading(true);
    api.get('/passes?withNoPass=true')
      .then(res => {
        setGuests(res.data.passes || []);
      })
      .catch(err => console.error('Error loading guest list:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const filteredGuests = search.trim()
    ? guests.filter(g => {
      const s = search.toLowerCase();
      const d = g.event?.date ? new Date(g.event.date) : null;
      const dateStrNumeric = d ? d.toLocaleDateString('en-IN') : '';
      const dateStrFull = d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).toLowerCase() : '';
      return (
        g.guestName?.toLowerCase().includes(s) ||
        g.guestEmail?.toLowerCase().includes(s) ||
        g.organization?.toLowerCase().includes(s) ||
        g.event?.title?.toLowerCase().includes(s) ||
        g.event?.eventMode?.toLowerCase().includes(s) ||
        dateStrNumeric.includes(s) ||
        dateStrFull.includes(s)
      );
    })
    : guests;

  const exportExcel = () => {
    const data = filteredGuests.map((g, idx) => ({
      'Sno': idx + 1,
      'Guest Name': g.guestName,
      'Designation': g.profession || '—',
      'Organisation': g.organization || '—',
      'Country': g.country || '—',
      'Work Email': g.guestEmail,
      'Personal Email': g.personalEmail || '—',
      'Phone Number': g.guestPhone ? (g.guestPhone.startsWith('+') ? g.guestPhone : `+${g.guestPhone}`) : '—',
      'Department': g.guestDepartment || '—',
      'Industry': g.industry || '—',
      'Office Address': g.address || '—',
      'Social Profile': g.linkedinId || '—',
      'Event Name': g.event?.title || '—',
      'Event Date': formatEventDate(g.event?.date, g.event?.endDate, g.event?.isMultiDay),
      'Event Host': g.event?.faculty?.name || '—',
      'Event venue': g.event?.venue || '—',
      'Event Mode': g.event?.eventMode || 'Offline',
      'Event Type': g.event?.type || '—'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Guest List');
    XLSX.writeFile(wb, `University_Guest_List_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel report exported');
  };

  const exportPDF = async () => {
    const toastId = toast.loading('Generating official audit report...');
    const ITEMS_PER_PAGE = 8;
    const chunks = [];
    for (let i = 0; i < filteredGuests.length; i += ITEMS_PER_PAGE) {
      chunks.push(filteredGuests.slice(i, i + ITEMS_PER_PAGE));
    }

    const pdf = new jsPDF('l', 'mm', 'a4');

    const getLinkedInHandle = (url) => {
      if (!url) return '—';
      try {
        const parts = url.replace(/\/$/, '').split('/');
        const handle = parts[parts.length - 1];
        return handle.startsWith('@') ? handle : `@${handle}`;
      } catch (e) { return url; }
    };

    try {
      for (let pageIdx = 0; pageIdx < chunks.length; pageIdx++) {
        const currentChunk = chunks[pageIdx];
        const exportRoot = document.createElement('div');
        exportRoot.style.position = 'absolute';
        exportRoot.style.top = '0';
        exportRoot.style.left = '-2000px';
        exportRoot.style.width = '1100px';
        exportRoot.style.backgroundColor = '#ffffff';
        exportRoot.style.padding = '30px';
        exportRoot.style.fontFamily = "'Plus Jakarta Sans', sans-serif";

        exportRoot.innerHTML = `
          <div style="border: 1px solid #e2e8f0; border-radius: 8px; height: 710px; display: flex; flex-direction: column; overflow: hidden;">
            <!-- Header -->
            <div style="padding: 20px 30px; border-bottom: 3px solid #005fa7; margin-bottom: 20px;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
                <img src="${logoBase64}" style="height: 60px; object-fit: contain;" />
                <div style="text-align: right;">
                  <div style="text-transform: uppercase; font-size: 10px; font-weight: 800; color: #005fa7; letter-spacing: 2px;">Official Document</div>
                  <div style="font-size: 28px; font-weight: 900; color: #0f172a;">Guest Audit Report</div>
                </div>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: flex-end;">
                <div>
                  <p style="color: #64748b; margin: 0; font-size: 14px; font-weight: 500;">
                    University Administrative Copy &nbsp;&bull;&nbsp; Date: ${new Date().toLocaleDateString('en-IN')}
                  </p>
                </div>
                <div style="text-align: right;">
                  <div style="font-size: 12px; color: #64748b; font-weight: 600;">Page ${pageIdx + 1} of ${chunks.length}</div>
                </div>
              </div>
            </div>

            <!-- Table -->
            <div style="flex-grow: 1; padding: 0 30px;">
              <table style="width: 100%; border-collapse: separate; border-spacing: 0; font-size: 11px;">
                <thead>
                  <tr>
                    <th style="padding: 15px 10px; background: #005fa7; color: #ffffff; text-align: center; font-weight: 700; border-radius: 6px 0 0 0;">S.NO</th>
                    <th style="padding: 15px 10px; background: #005fa7; color: #ffffff; text-align: center; font-weight: 700;">GUEST NAME</th>
                    <th style="padding: 15px 10px; background: #005fa7; color: #ffffff; text-align: center; font-weight: 700;">ORGANISATION</th>
                    <th style="padding: 15px 10px; background: #005fa7; color: #ffffff; text-align: center; font-weight: 700;">COUNTRY</th>
                    <th style="padding: 15px 10px; background: #005fa7; color: #ffffff; text-align: center; font-weight: 700;">EVENT</th>
                    <th style="padding: 15px 10px; background: #005fa7; color: #ffffff; text-align: center; font-weight: 700;">DATE</th>
                    <th style="padding: 15px 10px; background: #005fa7; color: #ffffff; text-align: center; font-weight: 700; border-radius: 0 6px 0 0;">SOCIAL PROFILE</th>
                  </tr>
                </thead>
                <tbody>
                  ${currentChunk.map((g, i) => `
                    <tr style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f8fafc'};">
                      <td style="padding: 12px 10px; border-bottom: 1px solid #f1f5f9; color: #64748b; font-weight: 600; text-align: center;">${String(pageIdx * ITEMS_PER_PAGE + i + 1).padStart(2, '0')}</td>
                      <td style="padding: 12px 10px; border-bottom: 1px solid #f1f5f9; color: #0f172a; font-weight: 700; text-align: center;">
                        ${g.guestName}
                        <div style="font-size: 9px; font-weight: 400; color: #64748b; margin-top: 2px;">${g.profession || '—'}</div>
                      </td>
                      <td style="padding: 12px 10px; border-bottom: 1px solid #f1f5f9; color: #334155; text-align: center;">${g.organization || '—'}</td>
                      <td style="padding: 12px 10px; border-bottom: 1px solid #f1f5f9; color: #334155; text-align: center;">${g.country || '—'}</td>
                      <td style="padding: 12px 10px; border-bottom: 1px solid #f1f5f9; color: #0f172a; font-weight: 600; text-align: center;">${g.event?.title || '—'}</td>
                      <td style="padding: 12px 10px; border-bottom: 1px solid #f1f5f9; color: #64748b; text-align: center;">${formatEventDate(g.event?.date, g.event?.endDate, g.event?.isMultiDay)}</td>
                      <td style="padding: 12px 10px; border-bottom: 1px solid #f1f5f9; color: #0076cf; font-family: monospace; font-size: 10px; font-weight: 700; text-align: center;">${getLinkedInHandle(g.linkedinId)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>

            <!-- Footer -->
            <div style="padding: 20px 30px; border-top: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; margin-top: auto;">
              <div style="font-size: 10px; color: #94a3b8;">
                &copy; ${new Date().getFullYear()} Guest Portal KRMU. All rights reserved.
              </div>
              <div style="font-size: 10px; color: #94a3b8; font-style: italic;">
                Internal Audit Copy - Page ${pageIdx + 1}
              </div>
            </div>
          </div>
        `;

        document.body.appendChild(exportRoot);
        await new Promise(r => setTimeout(r, 400));
        const canvas = await html2canvas(exportRoot, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
        document.body.removeChild(exportRoot);
        const imgData = canvas.toDataURL('image/png');
        if (pageIdx > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, 297, 210);
      }
      pdf.save(`Guest_Audit_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Official audit report exported', { id: toastId });
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('Failed to generate PDF', { id: toastId });
    }
  };

  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showExportMenu && !e.target.closest('.export-dropdown-wrap')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const isAdmin = ['iqac', 'super_admin'].includes(faculty?.role);
  const isAO = faculty?.role === 'ao';
  const pageSubtitle = (isAdmin || isAO) ? 'Full university guest list with event details' : 'Your personal guest list';

  if (loading) {
    return (
      <div className="page-shell">
        <div className="page-loading">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">Guest List</div>
          <div className="page-subtitle">{pageSubtitle}</div>
        </div>
        <div className="page-actions" style={{ gap: '0.75rem', alignItems: 'center' }}>
          {faculty?.role !== 'ao' && (
            <button type="button" className="btn btn-navy" onClick={() => setShowAddModal(true)}>
              + Add Guest
            </button>
          )}

          <div className="export-dropdown-wrap" style={{ position: 'relative' }}>
            <button
              className="btn btn-ghost"
              onClick={() => setShowExportMenu(!showExportMenu)}
              style={{ width: '42px', height: '42px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', fontWeight: 900 }}
            >
              ⋮
            </button>
            {showExportMenu && (
              <div className="card shadow-lg" style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                width: '180px',
                zIndex: 100,
                padding: '0.5rem',
                border: '1px solid var(--border-1)',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', padding: '0.5rem 0.75rem' }}>Export Options</div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => { exportExcel(); setShowExportMenu(false); }}
                  disabled={filteredGuests.length === 0}
                  style={{ justifyContent: 'flex-start', width: '100%', fontSize: '0.8125rem' }}
                >
                  <DownloadIcon style={{ width: '1rem', height: '1rem' }} />
                  Excel Report
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => { exportPDF(); setShowExportMenu(false); }}
                  disabled={filteredGuests.length === 0}
                  style={{ justifyContent: 'flex-start', width: '100%', fontSize: '0.8125rem' }}
                >
                  <DownloadIcon style={{ width: '1rem', height: '1rem' }} />
                  PDF Report
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="card toolbar" style={{ marginBottom: '1.5rem' }}>
          <div style={{ position: 'relative', width: '100%' }}>
            <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', display: 'flex' }}>
              <SearchIcon style={{ width: '1.25rem', height: '1.25rem' }} />
            </span>
            <input
              className="toolbar-input"
              placeholder="Search by name, organization, event or mode…"
              style={{ paddingLeft: '2.75rem', width: '100%' }}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="card" style={{ padding: 0 }}>
          {filteredGuests.length === 0 ? (
            <div style={{ padding: '4rem 2rem', textAlign: 'center', color: 'var(--text-3)' }}>
              <div style={{ fontSize: '1rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {search ? 'No matching guests found' : 'No guests in the list yet'}
              </div>
            </div>
          ) : (
            <div className="table-wrap" style={{ overflow: 'visible' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-1)', background: 'var(--surface-2)' }}>
                    <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-3)', width: '60px' }}>Sno</th>
                    <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-3)', minWidth: '180px' }}>Guest Details</th>
                    <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-3)', minWidth: '120px' }}>Phone Number</th>
                    <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-3)', minWidth: '150px' }}>Work Email</th>
                    <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-3)', minWidth: '200px' }}>Event Name</th>
                    <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-3)', minWidth: '110px' }}>Event Date</th>
                    <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-3)', minWidth: '80px' }}>Mode</th>
                    <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-3)', minWidth: '100px' }}>Social Profile</th>
                    <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-3)', minWidth: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGuests.map((g, idx) => (
                    <tr key={g._id} style={{ borderBottom: idx < filteredGuests.length - 1 ? '1px solid var(--border-1)' : 'none', position: 'relative', zIndex: openMenuId === g._id ? 50 : 1 }}>
                      <td style={{ padding: '1.25rem 1.5rem', fontSize: '0.875rem', color: 'var(--text-3)', fontWeight: 600 }}>{idx + 1}</td>
                      <td style={{ padding: '1.25rem 1.5rem', minWidth: '180px' }}>
                        <div style={{ fontWeight: 700, color: 'var(--text-1)', fontSize: '0.9375rem', lineHeight: 1.3 }}>{g.guestName}</div>
                        {g.profession && (
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginTop: '2px', fontWeight: 600 }}>{g.profession}</div>
                        )}
                        {g.organization && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: '1px' }}>{g.organization}</div>
                        )}
                      </td>
                      <td style={{ padding: '1.25rem 1.5rem', fontSize: '0.875rem', color: 'var(--text-2)' }}>
                        {g.guestPhone ? (g.guestPhone.startsWith('+') ? g.guestPhone : `+${g.guestPhone}`) : '—'}
                      </td>
                      <td style={{ padding: '1.25rem 1.5rem', fontSize: '0.8125rem', color: 'var(--text-3)', minWidth: '150px', wordBreak: 'break-all' }}>{g.guestEmail}</td>
                      <td style={{ padding: '1.25rem 1.5rem', minWidth: '200px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: '0.875rem', lineHeight: 1.4 }}>{g.event?.title || '—'}</div>
                      </td>
                      <td style={{ padding: '1.25rem 1.5rem', fontSize: '0.875rem', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                        {formatEventDate(g.event?.date, g.event?.endDate, g.event?.isMultiDay)}
                      </td>
                      <td style={{ padding: '1.25rem 1.5rem' }}>
                        <span className="event-type-pill" style={{
                          background: g.event?.eventMode === 'online' ? '#eff6ff' : (g.event?.eventMode === 'hybrid' ? '#f0fdf4' : '#f8fafc'),
                          color: g.event?.eventMode === 'online' ? '#1e40af' : (g.event?.eventMode === 'hybrid' ? '#166534' : '#475569'),
                          border: '1px solid currentColor',
                          opacity: 0.8,
                          textTransform: 'capitalize',
                          fontSize: '0.65rem',
                          padding: '0.125rem 0.5rem'
                        }}>
                          {g.event?.eventMode || 'Offline'}
                        </span>
                      </td>
                      <td style={{ padding: '1.25rem 1.5rem', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                        {g.linkedinId ? (
                          <a
                            href={g.linkedinId.startsWith('http') ? g.linkedinId : `https://${g.linkedinId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--navy)', textDecoration: 'none', fontWeight: 700 }}
                            onMouseOver={e => e.target.style.textDecoration = 'underline'}
                            onMouseOut={e => e.target.style.textDecoration = 'none'}
                          >
                            Open Link
                          </a>
                        ) : '—'}
                      </td>
                      <td style={{ textAlign: 'center', padding: '1.25rem 1.5rem' }}>
                        <button
                          type="button"
                          className="icon-btn"
                          title="View Details"
                          style={{ width: '2.5rem', height: '2.5rem', margin: '0 auto' }}
                          onClick={() => setViewPass(g)}
                        >
                          <EyeIcon style={{ width: '1.125rem', height: '1.125rem' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {viewPass && (
        <GuestDetailsModal
          pass={viewPass}
          onClose={() => setViewPass(null)}
        />
      )}

      {showAddModal && (
        <AddGuestModal
          faculty={faculty}
          onClose={() => setShowAddModal(false)}
          onSaved={() => {
            loadData();
            setShowAddModal(false);
          }}
        />
      )}
    </div>
  );
}


